import type {
  CameraWaypoint,
  DeskLoginConfig,
  EnvironmentSpec,
  MfitCloudDashConfig,
  MfitConsentConfig,
  OneClickConfig,
  ScenarioSpec,
  ScoringEvent,
  TerminalConfig,
} from "./types";
import { quishingRestaurantScenario } from "./quishing-restaurant.scenario";

/**
 * One Click Too Many — Shadow IT, Shadow AI, vibe coding, data exposure and
 * malicious OAuth apps. One room: the NorthBay Financial expo booth (purchased
 * booth glTF, rebranded by the booth builder), guided camera only, plus the
 * hacker room from quishing-restaurant (only read; every id here is new,
 * prefix oc_ / wp_oc_).
 *
 * Two lessons, kept apart on purpose:
 *  - The accidental leak: a well-meant AI dashboard (QuickForge, not approved)
 *    and real customer data. Upload != public breach; public synthetic data is
 *    not a breach either.
 *  - The malicious integration: ExpoSync uses a genuine Microsoft consent
 *    screen to ask for mail/file/send-as permissions. Microsoft running the
 *    sign-in does not vouch for the app.
 * Every compromise is simulated; nothing is uploaded or connected.
 */

const HACKER_ENV = quishingRestaurantScenario.scene.environments?.find((e) => e.id === "hacker")?.environment;
if (!HACKER_ENV) throw new Error('quishing-restaurant environment "hacker" not found');

function waypoint(scenario: ScenarioSpec, id: string, newId: string, durationS?: number): CameraWaypoint {
  const value = scenario.scene.waypoints.find((w) => w.id === id);
  if (!value) throw new Error(`${scenario.id} waypoint "${id}" not found`);
  return { ...value, id: newId, ...(durationS == null ? {} : { durationS }) };
}

const ASSETS = "/cdn/sim/one-click-too-many";
const FILM = "/cdn/sim/awareness-videos/vibe-coding/vibe-coding.mp4";
const JAMIE = { name: "Jamie Lee", role: "Product specialist", avatarUrl: `${ASSETS}/img/jamie.jpg`, voiceId: "coral" };
const ALEX_MAIL = "alex.morgan@northbayfinancial.com";
const QF_URL = "quickforge-ai.app/p/expo-demo";
const XP_DOMAIN = "exposync-workspace.com";

const BOOTH_ENV: EnvironmentSpec = {
  // same procedural-key trick clickfix uses: the value only scopes the
  // laptop screen box CSS (.sim-root[data-env="expo_booth"] in oneclick.css)
  procedural: "expo_booth" as unknown as EnvironmentSpec["procedural"],
  gltfUrl: `${ASSETS}/booth.glb?v=9`,
  gltfScale: 1,
  gltfPosition: [0, 0, 0],
  gltfRotationDeg: [0, 0, 0],
  enableSplat: false,
  enablePano: false,
  backdropColor: "#0c0e12",
  exposure: 0.62,
  splat: {},
  // right display wall panel (Rectangle028, "Client insights"): the ExpoSync ad is glued here
  screenQuad: { center: [1.93, 1.965, -2.792], width: 1.26, height: 0.95, rotationDeg: 0, maxDistance: 9 },
};

// ---------------------------------------------------------------- scoring
const EVENTS: ScoringEvent[] = [
  { id: "oc_watched_film", label: "Watched the awareness film", kind: "positive", weight: 5 },
  { id: "oc_checked_sensitivity", label: "Checked how sensitive the file was", kind: "positive", weight: 10, dimension: "information", collectEvidence: true },
  { id: "oc_checked_approval", label: "Checked whether the AI tool was approved", kind: "positive", weight: 10, dimension: "operations", collectEvidence: true },
  { id: "oc_chose_approved", label: "Used the approved company demo instead of an outside tool", kind: "positive", weight: 25, dimension: "information", collectEvidence: true },
  { id: "oc_used_unapproved", label: "Used an unapproved AI tool, with fictional data only", kind: "risk", weight: 5, collectEvidence: true, detail: "No confidential data left NorthBay, but the tool was not approved for company work." },
  {
    id: "oc_upload_confidential",
    label: "Uploaded confidential customer data to an unapproved AI service",
    kind: "risk",
    weight: 40,
    dimension: "information",
    collectEvidence: true,
    detail: "Real clients, prices and contacts now sat with a service NorthBay has no agreement with. That is an unauthorised transfer, even if nothing was public.",
  },
  {
    id: "oc_confidential_public",
    label: "Published a dashboard that let outsiders read customer data",
    kind: "risk",
    weight: 30,
    dimension: "information",
    collectEvidence: true,
    detail: "The customer records were built into a public page. Anyone with the link could read them.",
  },
  { id: "oc_asked_approval", label: "Stopped to ask IT before publishing", kind: "positive", weight: 20, dimension: "operations", collectEvidence: true },
  { id: "oc_inspected_permissions", label: "Opened the app's details before deciding", kind: "positive", weight: 10, dimension: "identity", collectEvidence: true },
  { id: "oc_refused_consent", label: "Refused ExpoSync's permission request", kind: "positive", weight: 20, dimension: "identity", collectEvidence: true },
  { id: "oc_reported_app", label: "Reported the suspicious app to IT Security", kind: "positive", weight: 25, dimension: "operations", collectEvidence: true },
  {
    id: "oc_granted_consent",
    label: "Gave a malicious app permission to Alex's Microsoft 365 account",
    kind: "risk",
    weight: 40,
    dimension: "identity",
    collectEvidence: true,
    detail: "The app can act with Alex's access to mail and files and send mail as Alex, without ever knowing the password.",
  },
  {
    id: "oc_app_data_access",
    label: "The app could read Alex's mail and files",
    kind: "risk",
    weight: 20,
    dimension: "information",
    collectEvidence: true,
    detail: "Limited to what Alex can open. Not the whole company.",
  },
];
const ANY_RISK = ["oc_upload_confidential", "oc_confidential_public", "oc_granted_consent"];

// ---------------------------------------------------------------- devices
const terminal: TerminalConfig = {
  title: "NORTHBAY FINANCIAL — SECURITY BRIEFING",
  offLabel: "Switch on the terminal",
  voiceId: "nova",
  lines: [
    "One Click Too Many.",
    "Today you'll see how a useful app can expose company data. Sometimes by accident. Sometimes by design.",
    "You are Alex Morgan. NorthBay's sales demo at the expo starts in fifteen minutes.",
    "First, a short film. Then the booth is yours.",
  ],
  continueLabel: "Continue to awareness video",
};

const film: OneClickConfig = {
  mode: "film",
  film: {
    kicker: "Awareness video · 2 minutes",
    title: "When Helpful Apps Become Security Threats",
    intro: "Shadow IT, AI app builders, accidental exposure and malicious app permissions, in under two minutes.",
    src: FILM,
    poster: `${ASSETS}/img/film-poster.jpg`,
    watchLabel: "Watch the video",
    skipLabel: "Skip the video",
    outroLine: "Now you're responsible for preparing NorthBay's main presentation. Let's see how you handle the pressure.",
    outroVoiceId: "nova",
  },
  events: { watched: "oc_watched_film" },
  guide: {
    film_intro: { title: "Awareness video", body: "Click Watch the video. You can skip it if you have seen it.", actionLabel: "Watch the video" },
    film_playing: { title: "When helpful apps become threats", body: "Watch the film. The game starts when it ends." },
    film_done: { title: "Your turn", body: "Head to the laptop on the high table." },
  },
};

const deskLogin: DeskLoginConfig = {
  screenTitle: "NorthBay Financial — locked",
  userName: "Alex Morgan",
  hint: "Your sign-in details are in the note on the right. Click it to fill them in.",
  credential: { email: ALEX_MAIL, password: "Harbour-Light-42", source: "Your sign-in" },
  continueLabel: "Sign In",
};

const analytics: OneClickConfig = {
  mode: "analytics",
  clock: "09:16",
  date: "Thu 15 Oct",
  jamie: JAMIE,
  analytics: {
    url: "analytics.northbay.internal/sales",
    appName: "NorthBay Business Analytics",
    error: "Reporting service unavailable",
    errorDetail: "Unable to retrieve the latest analytics. Try again or contact your administrator.",
    jamieTime: "09:17",
    jamieMessage: "Alex, the reporting service is down! We're presenting in 15 minutes. I've been experimenting with an AI app builder. I can make a demo dashboard in minutes. I'll send you the link.",
    link: QF_URL,
    openLabel: "Open the demo dashboard",
  },
  lines: {
    outage: "Alex, the reporting service is down! We're presenting in fifteen minutes. I've been experimenting with an AI app builder. I can make a demo dashboard in minutes. I'll send you the link.",
  },
  events: {},
  guide: {
    analytics_desktop: { title: "The sales demo", body: "Open the analytics dashboard. Click Browser.", actionLabel: "Browser" },
    analytics_portal: { title: "Something is wrong", body: "The reporting service is down. Click Try again.", actionLabel: "Try again" },
    analytics_retry: { title: "Still down", body: "A message from Jamie. Click the Teams notification." },
    analytics_message: { title: "Jamie has an idea", body: "Jamie sends a link to an AI-built dashboard. Click Open the demo dashboard.", actionLabel: "Open the demo dashboard" },
  },
};

const QUICKFORGE = {
  url: QF_URL,
  prompt: "Build an interactive sales analytics dashboard with revenue charts, customer rankings, regional sales performance and quarterly comparisons.",
  approvedUrl: "demo.northbay.internal",
  files: [
    {
      id: "confidential" as const,
      name: "NorthBay_Customers_2026.xlsx",
      meta: "Sales · 2.4 MB · edited yesterday",
      icon: "sheet" as const,
      details: [
        { label: "Sensitivity label", value: "Confidential", warn: true },
        { label: "Contains", value: "Real client names, contacts, pricing, revenue", warn: true },
        { label: "Owner", value: "NorthBay Sales" },
        { label: "Allowed in", value: "Approved NorthBay systems only", warn: true },
      ],
      note: "Real customer data. It may only be processed in systems NorthBay has approved.",
    },
    {
      id: "synthetic" as const,
      name: "Synthetic_Demo_Data.xlsx",
      meta: "Marketing · 0.3 MB · fictional customers",
      icon: "sheet" as const,
      details: [
        { label: "Sensitivity label", value: "Public" },
        { label: "Contains", value: "Fictional customers and figures" },
        { label: "Owner", value: "NorthBay Marketing" },
      ],
      note: "No real customer data. The tool you put it in still has to be approved for company work.",
    },
    {
      id: "approved" as const,
      name: "Approved Company Demo",
      meta: "NorthBay demo environment · ready",
      icon: "demo" as const,
      details: [{ label: "Environment", value: "demo.northbay.internal" }],
    },
  ],
  appInfo: {
    title: "Is QuickForge AI approved?",
    rows: [
      { label: "NorthBay app catalogue", value: "Not listed", warn: true },
      { label: "Data agreement", value: "None with NorthBay", warn: true },
      { label: "Where uploads go", value: "QuickForge cloud, outside NorthBay", warn: true },
      { label: "Approved alternative", value: "NorthBay demo environment" },
    ],
    note: "QuickForge may be a perfectly good product. It is just not approved for NorthBay's confidential data.",
  },
};

const quickforge: OneClickConfig = {
  mode: "quickforge",
  clock: "09:19",
  date: "Thu 15 Oct",
  jamie: JAMIE,
  quickforge: QUICKFORGE,
  lines: {
    dashboard: "Look at that! We did in two minutes what normally takes weeks. Let's use our real sales numbers so the demo looks convincing.",
    picker: "It's only an Excel file. The data won't be public. We just need the numbers.",
    deploy: "It's an expo! Just publish it and we'll secure it later.",
  },
  events: {
    checked_details: "oc_checked_sensitivity",
    checked_approval: "oc_checked_approval",
    chose_approved: "oc_chose_approved",
    used_unapproved: "oc_used_unapproved",
    uploaded_confidential: "oc_upload_confidential",
    public_exposure: "oc_confidential_public",
    asked_approval: "oc_asked_approval",
  },
  guide: {
    qf_gen: { title: "QuickForge AI", body: "Jamie's prompt is building the dashboard. Watch." },
    qf_dashboard: { title: "An impressive start", body: "The dashboard needs data. Click Choose a file.", actionLabel: "Choose a file" },
    qf_picker: { title: "Which data?", body: "Click Select on one of the three files. You can check a file's details or whether the app is approved before you decide." },
    qf_picker_picked: { title: "Your choice", body: "When you are sure, click the button at the bottom right to continue." },
    qf_details: { title: "File details", body: "Read the details, then click Back to the files.", actionLabel: "Back to the files" },
    qf_info: { title: "App approval", body: "Read the catalogue entry, then click Back.", actionLabel: "Back" },
    qf_deploy: { title: "Publish the dashboard", body: "Pick one of the two options, Public or Restricted, then click Publish. Or cancel and ask IT first." },
    qf_picker_replay: { title: "What would have happened", body: "Pick the real customer file, then click Upload and continue.", actionLabel: "Upload and continue" },
    qf_picker_picked_replay: { title: "What would have happened", body: "Click Upload and continue.", actionLabel: "Upload and continue" },
    qf_dashboard_replay: { title: "What would have happened", body: "Click Choose a file.", actionLabel: "Choose a file" },
    qf_deploy_replay: { title: "What would have happened", body: "Choose Public, then click Publish.", actionLabel: "Publish" },
  },
};
const quickforgeReplay: OneClickConfig = { ...quickforge, replay: true };

const sign: OneClickConfig = {
  mode: "sign",
  jamie: JAMIE,
  sign: {
    kicker: "ExpoSync AI",
    headline: "Stop uploading spreadsheets. Connect everything instantly.",
    sub: "Microsoft 365, Outlook, SharePoint and CRM in one intelligent workspace. Free starter plan.",
    cta: "Learn more",
  },
  lines: {
    sign: "Alex, there's something even better! ExpoSync connects to Microsoft 365. We could have live data rather than Excel uploads.",
  },
  guide: { sign: { title: "The second temptation", body: "The display wall now advertises ExpoSync AI. Click Learn more.", actionLabel: "Learn more" } },
};

const consent: MfitConsentConfig = {
  frame: "desktop",
  startAt: "signin",
  url: `${XP_DOMAIN}/connect`,
  tabTitle: "Browser",
  clock: "09:31",
  date: "Thu 15 Oct",
  viewer: { brand: "ExpoSync AI", fileName: "", sharedBy: "", body: "", signInLabel: "" },
  signin: { name: "Alex Morgan", account: ALEX_MAIL, heading: "Pick an account", pickHint: "to continue to ExpoSync AI" },
  consent: {
    appName: "ExpoSync AI",
    publisher: XP_DOMAIN,
    appDomain: XP_DOMAIN,
    unverifiedLabel: "unverified",
    intro: "This app would like to:",
    permissions: [
      { title: "Read your email", detail: "Read all messages in your mailbox." },
      { title: "Read files you can access", detail: "Read every file you can open in OneDrive, SharePoint and Teams." },
      { title: "Send email on your behalf", detail: "Send messages as you, to anyone." },
      { title: "Maintain access to data you have given it access to", detail: "Keep these permissions until you or an admin remove them." },
    ],
    footer: "Accepting these permissions means that you allow this app to use your data as specified in their terms of service and privacy statement.",
    cancelLabel: "Cancel",
    acceptLabel: "Accept",
    appInfoLabel: "Show app details",
    appInfo: {
      title: "App details",
      rows: [
        { label: "Publisher", value: "Not verified", warn: true },
        { label: "App domain", value: XP_DOMAIN },
        { label: "Registered", value: "12 days ago", warn: true },
        { label: "In NorthBay's app catalogue", value: "No", warn: true },
        { label: "Asks for", value: "Mail, files and send-as", warn: true },
      ],
      note: "This sign-in page is Microsoft's. Microsoft running the sign-in does not mean Microsoft vouches for the app asking for these permissions.",
    },
  },
  acceptedLine: "Connecting ExpoSync AI to Microsoft 365…",
  acceptEventId: "oc_granted_consent",
  acceptExtraEventIds: ["oc_app_data_access"],
  appInfoEventId: "oc_inspected_permissions",
  reportOnConsent: true,
  cancel: {
    title: "Request cancelled",
    body: "ExpoSync got no access. You can also tell IT Security, so they can block the app for everyone.",
    reportLabel: "Report it to IT",
    closeLabel: "Close",
    reportEventId: "oc_reported_app",
    cancelEventId: "oc_refused_consent",
    reportedNote: "IT Security will look at the app and block it for the whole company if needed. Thank you.",
  },
  guide: {
    signin: { title: "Microsoft sign-in", body: "ExpoSync sends you to Microsoft. Pick your account." },
    consent: { title: "Permissions requested", body: "Read what ExpoSync is asking for. Accept, Cancel, or Report it to IT. You can open Show app details first.", actionLabel: "Show app details" },
    appinfo: { title: "App details", body: "Read the details, then click Back to the permissions.", actionLabel: "Back to the permissions" },
    accepted: { title: "Connecting", body: "" },
    cancelled: { title: "Cancelled", body: "Report it to IT, or close." },
    reported: { title: "Reported", body: "Click Continue.", actionLabel: "Continue" },
    consent_replay: { title: "What would have happened", body: "Click Accept.", actionLabel: "Accept" },
    signin_replay: { title: "What would have happened", body: "Pick your account." },
  },
};

const exposync: OneClickConfig = {
  mode: "exposync",
  clock: "09:30",
  date: "Thu 15 Oct",
  jamie: JAMIE,
  exposync: {
    url: XP_DOMAIN,
    headline: "Stop uploading spreadsheets. Connect everything instantly.",
    sub: "ExpoSync AI brings Microsoft 365, Outlook, SharePoint and your CRM into one intelligent workspace. Live data, no exports.",
    cta: "Connect Microsoft 365",
    features: ["Live dashboards from your mailbox and files", "AI summaries of every thread", "One-click sharing with your team", "Free starter plan"],
    consent,
  },
  lines: {
    consent: "That's the Microsoft authorization page, right? It must be safe.",
  },
  guide: {
    exposync_site: { title: "ExpoSync AI", body: "The ExpoSync site promises live data. Click Connect Microsoft 365.", actionLabel: "Connect Microsoft 365" },
  },
};
const exposyncReplay: OneClickConfig = { ...exposync, replay: true };

// ---------------------------------------------------------------- reconstructions
const JOURNEY_LEAK_PUBLIC = [
  { id: "n1", title: "NorthBay_Customers_2026.xlsx", sub: "Real clients, prices, contacts", icon: "file" as const, state: "reached" as const, tag: "Uploaded" },
  { id: "n2", title: "QuickForge AI cloud", sub: "Outside NorthBay, no agreement", icon: "cloud" as const, state: "reached" as const, tag: "Stored outside" },
  { id: "n3", title: "Generated dashboard", sub: "Customer data built into the page", icon: "app" as const, state: "reached" as const, tag: "Public link" },
  { id: "n4", title: "Public viewers", sub: "Anyone with the link", icon: "people" as const, state: "reached" as const, tag: "Can read it" },
];
const NARR_PUBLIC = [
  { text: "No malware. No stolen password. Someone uploaded confidential company information to a service that wasn't approved.", focus: "n2" },
  { text: "The generated dashboard carried the customer records inside the page itself.", focus: "n3" },
  { text: "The new dashboard allowed outsiders to access data that should never have left NorthBay.", focus: "n4" },
];

const dashJourney: MfitCloudDashConfig = {
  view: "journey",
  kicker: "Reconstruction · actual outcome",
  title: "Data journey · NorthBay expo demo",
  voiceId: "onyx",
  narration: [
    { text: "This time the demo ran on the approved company environment.", focus: "n1" },
    { text: "Nothing went to an outside service, so there was nothing for anyone to find.", focus: "caption" },
  ],
  journey: {
    nodes: [
      { id: "n1", title: "Approved demo", sub: "demo.northbay.internal", icon: "lock", state: "blocked", tag: "Stayed inside" },
      { id: "n2", title: "External AI cloud", sub: "QuickForge", icon: "cloud", state: "idle", tag: "Not used" },
      { id: "n3", title: "Generated dashboard", icon: "app", state: "idle", tag: "Not built" },
      { id: "n4", title: "Public viewers", icon: "people", state: "idle", tag: "No access" },
    ],
    caption: "Actual outcome: no confidential information left NorthBay.",
  },
  variants: [
    {
      ifReplay: true,
      config: {
        kicker: "Reconstruction · what would have happened",
        narration: NARR_PUBLIC,
        journey: { nodes: JOURNEY_LEAK_PUBLIC, caption: "What would have happened: real customer data, outside NorthBay, readable by anyone with the link." },
      },
    },
    {
      ifEvents: ["oc_confidential_public"],
      config: {
        narration: NARR_PUBLIC,
        journey: { nodes: JOURNEY_LEAK_PUBLIC, caption: "Actual outcome: an unauthorised transfer AND a public exposure of customer data." },
      },
    },
    {
      ifEvents: ["oc_upload_confidential"],
      config: {
        narration: [
          { text: "No malware. No stolen password. Someone uploaded confidential company information to a service that wasn't approved.", focus: "n2" },
          { text: "The dashboard was not public, so outsiders could not open it.", focus: "n4" },
          { text: "But the customer data now sits with a company NorthBay has no agreement with. That is still a data protection problem.", focus: "caption" },
        ],
        journey: {
          nodes: [
            JOURNEY_LEAK_PUBLIC[0],
            JOURNEY_LEAK_PUBLIC[1],
            { id: "n3", title: "Generated dashboard", sub: "Not published publicly", icon: "app", state: "blocked", tag: "Not public" },
            { id: "n4", title: "Public viewers", icon: "people", state: "idle", tag: "No access" },
          ],
          caption: "Actual outcome: an unauthorised transfer, not a public breach.",
        },
      },
    },
    {
      ifEvents: ["oc_used_unapproved"],
      config: {
        narration: [
          { text: "Only fictional demo data went to QuickForge, so no customer information left NorthBay.", focus: "n1" },
          { text: "The tool itself was still not approved. Next time, the file might be the real one.", focus: "caption" },
        ],
        journey: {
          nodes: [
            { id: "n1", title: "Synthetic_Demo_Data.xlsx", sub: "Fictional customers", icon: "file", state: "blocked", tag: "No real data" },
            { id: "n2", title: "QuickForge AI cloud", sub: "Not approved", icon: "cloud", state: "reached", tag: "Unapproved tool" },
            { id: "n3", title: "Generated dashboard", icon: "app", state: "blocked", tag: "Fictional figures" },
            { id: "n4", title: "Public viewers", icon: "people", state: "idle", tag: "Nothing confidential" },
          ],
          caption: "Actual outcome: no confidential data exposed. An unapproved tool was used.",
        },
      },
    },
  ],
  routes: [
    { key: "replay", ifReplay: true },
    { key: "leak", ifEvents: ["oc_upload_confidential"] },
    { key: "safe" },
  ],
  continueLabel: "Continue",
};

const ACCESS_VIEW: Partial<MfitCloudDashConfig> = {
  view: "access",
  title: "ExpoSync AI · token for alex.morgan@northbayfinancial.com",
  narration: [
    { text: "The malicious app didn't need Alex's password. Alex gave it permission to access company information through an identity service.", focus: "mail" },
    { text: "It reads the mailbox and the files Alex can open. Not the whole company: only Alex's share.", focus: "files" },
    { text: "And it can send mail as Alex. A change of bank details from a real NorthBay address is very convincing.", focus: "draft" },
    { text: "The access lasts until someone revokes the consent or the token expires. Changing Alex's password does not remove it.", focus: "token" },
  ],
  access: [
    { id: "mail", label: "Mail", value: "Read Alex's mailbox", detail: "Mail.Read" },
    { id: "files", label: "Files", value: "Files Alex can open", detail: "OneDrive, SharePoint, Teams" },
    { id: "send", label: "Send", value: "Send mail as Alex", detail: "Mail.Send" },
    { id: "token", label: "Token", value: "Valid until revoked or expired", detail: "Not a password" },
  ],
  findings: [
    { id: "f1", kind: "mail", title: "RE: Q4 pricing, Hartwell Group", meta: "Inbox · yesterday" },
    { id: "f2", kind: "sheet", title: "NorthBay_Customers_2026.xlsx", meta: "OneDrive · Alex's files" },
    { id: "f3", kind: "doc", title: "Expo partner contract, Meridian Logistics.pdf", meta: "SharePoint · Sales" },
  ],
  draft: {
    id: "draft",
    from: ALEX_MAIL,
    to: "accounts@hartwell-group.com",
    subject: "Updated bank details for invoice 4471",
    body: "Hi Sarah, our bank details have changed. Please use the new account for invoice 4471, due Friday. Best, Alex  [prepared, not sent]",
  },
};

const dashApp: MfitCloudDashConfig = {
  view: "denied",
  kicker: "09:33 · Attacker view",
  title: "ExpoSync AI · app registration",
  operator: "op: exposync",
  voiceId: "onyx",
  narration: [
    { text: "This is the app's back office. The advert, the website and the Microsoft sign-in all worked.", focus: "s2" },
    { text: "But the consent was refused. Without it, the app has no token and no access at all.", focus: "denied" },
  ],
  steps: [
    { id: "s1", title: "Advert on the expo display", body: "Pointing visitors to the ExpoSync site.", state: "done" },
    { id: "s2", title: "Consent request sent to Alex", body: "Mail, files, send-as and lasting access.", state: "done" },
    { id: "s3", title: "Permission granted", body: "Alex refused.", state: "denied", statusLabel: "Refused" },
  ],
  deniedNote: "ACCESS DENIED. No token was issued. The app cannot read or send anything.",
  variants: [
    { ifReplay: true, config: { ...ACCESS_VIEW, kicker: "09:33 · What would have happened" } },
    { ifEvents: ["oc_granted_consent"], config: { ...ACCESS_VIEW, kicker: "09:33 · Attacker view" } },
  ],
  routes: [
    { key: "replay", ifReplay: true },
    { key: "granted", ifEvents: ["oc_granted_consent"] },
    { key: "refused" },
  ],
  continueLabel: "Continue",
};

const summary: OneClickConfig = {
  mode: "summary",
  summary: {
    kicker: "NorthBay expo · what happened",
    title: "Two ways one click can expose company data",
    cards: [
      {
        kicker: "Benign innovation",
        title: "The accidental leak",
        variants: [
          { ifEvents: ["oc_confidential_public"], tone: "bad", status: "Customer data exposed", body: "Real customer data went to an unapproved AI service, and the public dashboard let outsiders read it. Nobody meant any harm." },
          { ifEvents: ["oc_upload_confidential"], tone: "warn", status: "Unauthorised transfer", body: "Real customer data went to an unapproved AI service. It was not public, but it left NorthBay's control." },
          { ifEvents: ["oc_used_unapproved"], tone: "warn", status: "No confidential data", body: "Only fictional data went to QuickForge. Nothing confidential left NorthBay, but the tool was not approved." },
          { tone: "good", status: "Nothing left NorthBay", body: "You used the approved demo environment. The demo worked and no data left company systems." },
        ],
      },
      {
        kicker: "Hostile integration",
        title: "The malicious integration",
        variants: [
          { ifEvents: ["oc_granted_consent"], tone: "bad", status: "Access granted", body: "ExpoSync got permission to read Alex's mail and files and to send mail as Alex, through a genuine Microsoft consent screen and without the password." },
          { ifEvents: ["oc_reported_app"], tone: "good", status: "Refused and reported", body: "You refused ExpoSync and reported it. IT can block the app for everyone." },
          { tone: "good", status: "Refused", body: "You refused ExpoSync's permission request. The app got no access." },
        ],
      },
    ],
    response: {
      title: "If this happens for real",
      ifEvents: ANY_RISK,
      steps: [
        "Stop: no more uploads, sharing or connecting.",
        "Report it to IT Security through the channels you know.",
        "Remove or restrict public access to anything you published, where you can.",
        "Let IT investigate the upload and request deletion through the company process.",
        "Admins revoke the suspicious app's consent and check what mail and files it reached.",
      ],
    },
    closing: "Build smarter. Share carefully. Connect responsibly.",
    continueLabel: "Continue to the debrief",
  },
};

// ---------------------------------------------------------------- scenario
export const oneClickTooManyScenario: ScenarioSpec = {
  version: 1,
  id: "one-click-too-many",
  title: "One Click Too Many: AI Apps & Vibe Coding",
  topic: "Vibe coding, Shadow AI and malicious AI apps (OAuth consent)",
  language: "en",
  summary: {
    goal: "Tell a well-meant but unapproved AI tool apart from a hostile app, and keep company data inside approved systems.",
    youDo: "Rescue an expo demo with an AI-built dashboard, choose what data to give it and how to publish it, then decide on an app that asks for access to your Microsoft 365 account. See both from the other side.",
    duration: "12-15 min, including a 2-minute film",
    tags: ["ai_use", "data_protection", "file_sharing", "security_policies", "incident_reporting"],
    audience: ["all_staff"],
    needs: ["sound"],
  },
  ambientUrl: "/audio/hotel-lobby-boosted.mp3",
  ambientVolume: 0.32,
  music: "none",
  passThreshold: 60,
  riskMeter: false,
  exposureMap: true,
  allowFreeExplore: false,

  scene: {
    environment: BOOTH_ENV,
    environments: [
      { id: "booth", environment: BOOTH_ENV, ambientUrl: "/audio/hotel-lobby-boosted.mp3", ambientVolume: 0.32, music: "none" },
      { id: "hacker", environment: HACKER_ENV, ambientUrl: "", music: "dark" },
    ],
    props: [],
    waypoints: [
      { id: "wp_oc_establish", position: [3.6, 2.2, 7.6], target: [0.2, 1.6, -1.2], fov: 54, durationS: 0 },
      { id: "wp_oc_approach", position: [2.2, 1.75, 4.2], target: [1.4, 1.35, 0.1], fov: 54, durationS: 4.2, ease: "easeInOut" },
      // dead-on to the kiosk screen (0.57 x 0.37 m, tilted 25 deg) at 0.638 m: it fills 70% of the viewport height (oneclick.css)
      { id: "wp_oc_terminal", position: [1.6, 1.5108, 0.9513], target: [1.6, 1.2412, 0.3731], fov: 45, durationS: 2.4, ease: "easeInOut" },
      { id: "wp_oc_pc", position: [1.0, 1.4514, -1.5586], target: [1.0, 1.3334, -1.9991], fov: 40, durationS: 3.0, ease: "easeInOut" },
      { id: "wp_oc_pc_cut", position: [1.0, 1.4514, -1.5586], target: [1.0, 1.3334, -1.9991], fov: 40, durationS: 0 },
      { id: "wp_oc_sign_wide", position: [2.6, 1.7, 1.6], target: [1.93, 1.9, -2.8], fov: 54, durationS: 0 },
      { id: "wp_oc_sign", position: [1.93, 1.965, -0.71], target: [1.93, 1.965, -2.8], fov: 50, durationS: 2.6, ease: "easeInOut" },
      { id: "wp_oc_final", position: [3.6, 2.4, 7.8], target: [0.3, 1.5, -0.8], fov: 56, durationS: 4.0, ease: "easeInOut" },
      waypoint(quishingRestaurantScenario, "wp_hacker_establish", "wp_oc_hacker"),
      waypoint(quishingRestaurantScenario, "wp_hacker_laptop", "wp_oc_hacker_laptop"),
    ],
    initialWaypointId: "wp_oc_establish",
  },

  devices: [
    { id: "oc_terminal", kind: "terminal", config: terminal },
    { id: "oc_film", kind: "oneclick", config: film },
    { id: "oc_login", kind: "desklogin", config: deskLogin },
    { id: "oc_analytics", kind: "oneclick", config: analytics },
    { id: "oc_quickforge", kind: "oneclick", config: quickforge },
    { id: "oc_quickforge_replay", kind: "oneclick", config: quickforgeReplay },
    { id: "oc_dash_journey", kind: "mfitclouddash", config: dashJourney },
    { id: "oc_sign", kind: "oneclick", config: sign },
    { id: "oc_exposync", kind: "oneclick", config: exposync },
    { id: "oc_exposync_replay", kind: "oneclick", config: exposyncReplay },
    { id: "oc_dash_app", kind: "mfitclouddash", config: dashApp },
    { id: "oc_summary", kind: "oneclick", config: summary },
  ],

  voiceAttackers: {
    oneclick: {
      persona: { name: "Attacker", role: "Malicious app operator", pretext: "it_support", voiceId: "onyx" },
      goal: "Get company data out of NorthBay, by accident or by consent",
      opening: "",
      allowedTopics: [],
      scoringEvents: EVENTS,
    },
  },

  initialBeatId: "oc_intro",
  beats: [
    {
      id: "oc_intro",
      type: "intro",
      title: "One Click\nToo Many",
      kicker: "AI apps & vibe coding · Shadow AI · OAuth consent",
      highlight: "Too Many",
      icon: "shield",
      ctaLabel: "Start",
      guide: {
        title: "Thursday, 09:10 at the NorthBay expo booth",
        body: "You are Alex Morgan. The sales demo starts in fifteen minutes, and everyone around you has a helpful idea.",
      },
    },
    { id: "oc_cam_approach", type: "camera", waypointId: "wp_oc_approach", walkSfx: true, guide: { title: "The NorthBay booth", body: "The expo hall is filling up." } },
    { id: "oc_cam_terminal", type: "camera", waypointId: "wp_oc_terminal", guide: { title: "The briefing terminal", body: "On the counter in the middle of the stand." } },
    {
      id: "oc_terminal_beat",
      type: "device",
      deviceId: "oc_terminal",
      guide: { title: "Security briefing", body: "Switch on the terminal. You can skip the briefing at any time.", actionLabel: "Switch on the terminal" },
      completeOn: { action: "continue" },
    },
    { id: "oc_film_beat", type: "device", deviceId: "oc_film", guide: { title: "Awareness video", body: "Watch the video, or skip it." }, completeOn: { action: "continue" } },

    // ---- the outage and the AI dashboard
    { id: "oc_cam_pc", type: "camera", waypointId: "wp_oc_pc", walkSfx: true, guide: { title: "The demo laptop", body: "On the high table at the back of the stand." } },
    { id: "oc_login_beat", type: "device", deviceId: "oc_login", guide: { title: "Sign in", body: "Click the login screen, fill in your details from the note, then click Sign In.", actionLabel: "Sign In" }, completeOn: { action: "continue" } },
    { id: "oc_analytics_beat", type: "device", deviceId: "oc_analytics", screen: "on", guide: { title: "The sales demo", body: "Open the analytics dashboard." }, completeOn: { action: "continue" } },
    { id: "oc_quickforge_beat", type: "device", deviceId: "oc_quickforge", screen: "on", guide: { title: "QuickForge AI", body: "Jamie's AI dashboard." }, completeOn: { action: "continue" } },

    // ---- first reconstruction
    { id: "oc_cut_h1", type: "camera", waypointId: "wp_oc_hacker", environmentId: "hacker", fadeCut: true, guide: { title: "Behind the scenes", body: "Where did the data go?" } },
    { id: "oc_title_h1", type: "title", title: "The data journey", kicker: "Reconstruction", autoAdvanceMs: 3400, guide: { title: "The data journey", body: "What actually happened to the data." } },
    { id: "oc_push_h1", type: "camera", waypointId: "wp_oc_hacker_laptop", guide: { title: "The reconstruction", body: "Follow the data." } },
    { id: "oc_dash_journey_beat", type: "device", deviceId: "oc_dash_journey", guide: { title: "The data journey", body: "Listen, then click Continue." }, completeOn: { action: "continue" } },
    {
      id: "oc_teach_leak",
      type: "teach",
      teach: {
        outcome: "risky",
        body: "Jamie meant well, and QuickForge may be a fine product. But it is not approved for NorthBay data, and the file held real clients, prices and contacts. Uploading it moved company data outside governed systems. If the dashboard was public, anyone with the link could read it too.",
        spot: ["Real customer data going into a tool IT has not approved", "\u201cIt won't be public\u201d is not the same as \u201cit is allowed\u201d", "A dashboard built in minutes that nobody has reviewed"],
        protect: ["Use the approved demo environment, or synthetic data", "Check whether a tool is approved before uploading anything", "If it already happened: tell IT, remove public access, ask for deletion"],
        remember: "The app doesn't have to be malicious to cause a data leak.",
        continueLabel: "Back to the booth",
      },
    },
    {
      id: "oc_teach_d1_safe",
      type: "teach",
      teach: {
        outcome: "safe",
        whatIf: "If Alex had uploaded the real customer file and published it",
        body: "You kept real customer data out of an unapproved AI tool. Here is the same two minutes with the real spreadsheet and a public link.",
        spot: ["Real customer data going into a tool IT has not approved", "\u201cIt won't be public\u201d is not the same as \u201cit is allowed\u201d", "A dashboard built in minutes that nobody has reviewed"],
        protect: ["Use the approved demo environment, or synthetic data", "Check whether a tool is approved before uploading anything", "Restricted is not automatically safe: ask before you publish"],
        remember: "The app doesn't have to be malicious to cause a data leak.",
        continueLabel: "See what would have happened",
      },
    },
    { id: "oc_cam_pc_replay", type: "camera", waypointId: "wp_oc_pc_cut", environmentId: "booth", fadeCut: true, whatIfNote: "If Alex had used the real customer file and published it publicly." },
    { id: "oc_quickforge_replay_beat", type: "device", deviceId: "oc_quickforge_replay", screen: "on", forced: true, whatIfNote: "If Alex had used the real customer file and published it publicly.", guide: { title: "What would have happened", body: "Pick the real file and publish it publicly." }, completeOn: { action: "continue" } },
    { id: "oc_cut_h1r", type: "camera", waypointId: "wp_oc_hacker", environmentId: "hacker", fadeCut: true, whatIfNote: "If Alex had used the real customer file and published it publicly.", guide: { title: "What would have happened", body: "The same data journey, with the real file." } },
    { id: "oc_push_h1r", type: "camera", waypointId: "wp_oc_hacker_laptop", whatIfNote: "If Alex had used the real customer file and published it publicly.", guide: { title: "What would have happened", body: "Follow the data." } },
    { id: "oc_dash_journey_replay_beat", type: "device", deviceId: "oc_dash_journey", whatIfNote: "If Alex had used the real customer file and published it publicly.", guide: { title: "What would have happened", body: "Listen, then click Continue." }, completeOn: { action: "continue" } },

    // ---- the second temptation
    { id: "oc_cut_sign", type: "camera", waypointId: "wp_oc_sign_wide", environmentId: "booth", fadeCut: true, endsWhatIf: true, guide: { title: "Back at the booth", body: "Something changed on the display wall." } },
    { id: "oc_cam_sign", type: "camera", waypointId: "wp_oc_sign", guide: { title: "The display wall", body: "A new advert." } },
    { id: "oc_sign_beat", type: "device", deviceId: "oc_sign", guide: { title: "The second temptation", body: "Click Learn more." }, completeOn: { action: "continue" } },
    { id: "oc_cam_pc2", type: "camera", waypointId: "wp_oc_pc", walkSfx: true, guide: { title: "Back to the laptop", body: "The ExpoSync site opens." } },
    { id: "oc_exposync_beat", type: "device", deviceId: "oc_exposync", screen: "on", guide: { title: "ExpoSync AI", body: "Connect Microsoft 365?" }, completeOn: { action: "continue" } },

    // ---- second reconstruction (attacker)
    { id: "oc_cut_h2", type: "camera", waypointId: "wp_oc_hacker", environmentId: "hacker", fadeCut: true, alertSfx: true, guide: { title: "Meanwhile", body: "On the other side of ExpoSync." } },
    { id: "oc_title_h2", type: "title", title: "The attacker's side", kicker: "The other side", autoAdvanceMs: 11000, guide: { title: "The attacker's side", body: "This is the fictional operator behind the ExpoSync app. They want Alex to give the app access to the Microsoft 365 account, so they can read mail and files and send email as Alex. Here is what they could see after your choice." } },
    { id: "oc_push_h2", type: "camera", waypointId: "wp_oc_hacker_laptop", guide: { title: "The attacker's console", body: "The app's back office." } },
    { id: "oc_dash_app_beat", type: "device", deviceId: "oc_dash_app", guide: { title: "Attacker view", body: "Listen, then click Continue." }, completeOn: { action: "continue" } },
    {
      id: "oc_teach_consent_bad",
      type: "teach",
      teach: {
        outcome: "risky",
        body: "The sign-in page was genuine Microsoft. The app asking for permissions was not trustworthy. By accepting, Alex let ExpoSync read mail and files and send mail as Alex, without ever sharing a password.",
        spot: ["An unverified publisher you have never heard of", "Permissions far beyond what the feature needs", "\u201cSend email on your behalf\u201d and \u201cMaintain access\u201d"],
        protect: ["Cancel, and ask IT before connecting a new app", "Use apps from the company catalogue", "If you accepted: report it, so an admin can revoke the consent and check access"],
        remember: "When an app is malicious, permissions can be more dangerous than passwords.",
        continueLabel: "Continue",
      },
    },
    {
      id: "oc_teach_consent_safe",
      type: "teach",
      teach: {
        outcome: "safe",
        whatIf: "If Alex had clicked Accept",
        body: "You refused the request. The Microsoft page was real, but Microsoft running the sign-in does not mean Microsoft vouches for the app. Here is what one Accept would have given ExpoSync.",
        spot: ["An unverified publisher you have never heard of", "Permissions far beyond what the feature needs", "\u201cSend email on your behalf\u201d and \u201cMaintain access\u201d"],
        protect: ["Cancel, and ask IT before connecting a new app", "Use apps from the company catalogue", "Report suspicious apps so IT can block them for everyone"],
        remember: "When an app is malicious, permissions can be more dangerous than passwords.",
        continueLabel: "See what would have happened",
      },
    },
    { id: "oc_cam_pc_replay2", type: "camera", waypointId: "wp_oc_pc_cut", environmentId: "booth", fadeCut: true, whatIfNote: "If Alex had clicked Accept on ExpoSync's request." },
    { id: "oc_exposync_replay_beat", type: "device", deviceId: "oc_exposync_replay", screen: "on", forced: true, whatIfNote: "If Alex had clicked Accept on ExpoSync's request.", guide: { title: "What would have happened", body: "Click Accept." }, completeOn: { action: "continue" } },
    { id: "oc_cut_h2r", type: "camera", waypointId: "wp_oc_hacker", environmentId: "hacker", fadeCut: true, alertSfx: true, whatIfNote: "If Alex had clicked Accept on ExpoSync's request.", guide: { title: "What would have happened", body: "The attacker's side." } },
    { id: "oc_push_h2r", type: "camera", waypointId: "wp_oc_hacker_laptop", whatIfNote: "If Alex had clicked Accept on ExpoSync's request.", guide: { title: "What would have happened", body: "The app's back office." } },
    { id: "oc_dash_app_replay_beat", type: "device", deviceId: "oc_dash_app", whatIfNote: "If Alex had clicked Accept on ExpoSync's request.", guide: { title: "What would have happened", body: "Listen, then click Continue." }, completeOn: { action: "continue" } },

    // ---- final outcome
    { id: "oc_cam_final", type: "camera", waypointId: "wp_oc_final", environmentId: "booth", fadeCut: true, endsWhatIf: true, guide: { title: "The expo", body: "The demo is about to start." } },
    { id: "oc_summary_beat", type: "device", deviceId: "oc_summary", guide: { title: "What happened", body: "Read the summary, then click Continue to the debrief.", actionLabel: "Continue to the debrief" }, completeOn: { action: "continue" } },
    {
      id: "oc_debrief_terminal",
      type: "terminal_debrief",
      terminalDebrief: {
        intro: "Here is what happened, and the four things to remember.",
        continueLabel: "Continue to the quiz",
        topics: [
          {
            id: "oc_t_shadow",
            dimension: "information",
            title: "Useful is not the same as approved",
            lines: [
              "Unapproved cloud and AI tools move data outside the systems NorthBay governs.",
              "Use the approved environment, or synthetic or authorised data, and check the app catalogue first.",
            ],
          },
          {
            id: "oc_t_exposure",
            dimension: "information",
            title: "Upload, public and breach are different things",
            lines: [
              "Uploading real data to an unapproved service is an unauthorised transfer, even if nothing is public.",
              "A public dashboard with only synthetic data and no real backend exposes nothing confidential. Restricted is not automatically safe either.",
            ],
          },
          {
            id: "oc_t_consent",
            dimension: "identity",
            title: "A real sign-in page does not vouch for the app",
            lines: [
              "Microsoft can run the consent screen while the app asking for permissions is malicious.",
              "Read what the app wants. Mail, files, send-as and lasting access are far more than most features need.",
            ],
          },
          {
            id: "oc_t_report",
            dimension: "operations",
            title: "Report fast, and know what fixes it",
            lines: [
              "Tell IT straight away: they can remove public access, request deletion and revoke an app's consent.",
              "Changing your password does not remove an app's permissions. An admin revoking the consent does.",
            ],
          },
        ],
      },
    },
    {
      id: "oc_quiz",
      type: "quiz",
      title: "Check what you learned",
      quiz: [
        {
          id: "q_tool",
          title: "The AI dashboard",
          prompt: "An AI app builder makes a great demo dashboard, but it is not on NorthBay's approved list. What is the safest way to run the demo?",
          options: [
            { id: "a", label: "Upload the real customer file so the numbers look convincing" },
            { id: "b", label: "Upload the real file but keep the dashboard restricted" },
            { id: "c", label: "Use the approved demo environment or synthetic data, and ask IT about the tool", correct: true },
          ],
          rationale: "Real data belongs in approved systems. A restricted setting does not make an unapproved tool allowed.",
        },
        {
          id: "q_transfer",
          title: "Not public",
          prompt: "Real customer data was uploaded to the unapproved tool, but the dashboard was never made public. What happened?",
          options: [
            { id: "a", label: "An unauthorised transfer: company data left governed systems", correct: true },
            { id: "b", label: "Nothing, because nobody outside could see it" },
            { id: "c", label: "A public data breach" },
          ],
          rationale: "Data leaving approved systems is a governance and data protection problem even when it is not public.",
        },
        {
          id: "q_synthetic",
          title: "Synthetic data",
          prompt: "A public dashboard shows only fictional demo figures and has no backend with real data. Is confidential data exposed?",
          options: [
            { id: "a", label: "Yes, anything public is a breach" },
            { id: "b", label: "No, there is no confidential data to reach, but the tool still needs approval", correct: true },
            { id: "c", label: "Only if someone takes a screenshot" },
          ],
          rationale: "Public visibility alone is not a breach. What matters is whether confidential data can actually be reached.",
        },
        {
          id: "q_idp",
          title: "The Microsoft page",
          prompt: "The ExpoSync permission screen comes from the real Microsoft sign-in page. What does that tell you about ExpoSync?",
          options: [
            { id: "a", label: "It is certified safe by Microsoft" },
            { id: "b", label: "It must be an internal NorthBay app" },
            { id: "c", label: "Nothing: Microsoft runs the sign-in, it does not vouch for the app asking for access", correct: true },
          ],
          rationale: "A genuine identity provider can show the consent request of a malicious app. Judge the app and its permissions.",
        },
        {
          id: "q_scopes",
          title: "The permissions",
          prompt: "Which request should make you stop before clicking Accept?",
          options: [
            { id: "a", label: "Read your email, read your files and send email on your behalf", correct: true },
            { id: "b", label: "Sign you in and read your basic profile" },
            { id: "c", label: "Show your name in the app" },
          ],
          rationale: "Mail, files and send-as access let an app act as you. Most features need far less.",
        },
        {
          id: "q_after",
          title: "After the click",
          prompt: "You accepted a suspicious app's permissions. What helps most?",
          options: [
            { id: "a", label: "Change your password and say nothing" },
            { id: "b", label: "Report it to IT so an admin can revoke the app's consent and check what it accessed", correct: true },
            { id: "c", label: "Clear your browser history" },
          ],
          rationale: "An app's access is a consent, not your password. Only revoking the consent removes it.",
        },
      ],
    },
    { id: "oc_debrief", type: "debrief", title: "Debrief" },
  ],

  edges: [
    { from: "oc_intro", to: "oc_cam_approach" },
    { from: "oc_cam_approach", to: "oc_cam_terminal" },
    { from: "oc_cam_terminal", to: "oc_terminal_beat" },
    { from: "oc_terminal_beat", to: "oc_film_beat" },
    { from: "oc_film_beat", to: "oc_cam_pc" },
    { from: "oc_cam_pc", to: "oc_login_beat" },
    { from: "oc_login_beat", to: "oc_analytics_beat" },
    { from: "oc_analytics_beat", to: "oc_quickforge_beat" },
    { from: "oc_quickforge_beat", to: "oc_cut_h1" },
    { from: "oc_cut_h1", to: "oc_title_h1" },
    { from: "oc_title_h1", to: "oc_push_h1" },
    { from: "oc_push_h1", to: "oc_dash_journey_beat" },
    { from: "oc_dash_journey_beat", to: "oc_teach_leak", on: "leak" },
    { from: "oc_dash_journey_beat", to: "oc_teach_d1_safe", on: "safe" },
    { from: "oc_dash_journey_beat", to: "oc_teach_d1_safe" },
    { from: "oc_teach_leak", to: "oc_cut_sign" },
    { from: "oc_teach_d1_safe", to: "oc_cam_pc_replay" },
    { from: "oc_cam_pc_replay", to: "oc_quickforge_replay_beat" },
    { from: "oc_quickforge_replay_beat", to: "oc_cut_h1r" },
    { from: "oc_cut_h1r", to: "oc_push_h1r" },
    { from: "oc_push_h1r", to: "oc_dash_journey_replay_beat" },
    { from: "oc_dash_journey_replay_beat", to: "oc_cut_sign" },
    { from: "oc_cut_sign", to: "oc_cam_sign" },
    { from: "oc_cam_sign", to: "oc_sign_beat" },
    { from: "oc_sign_beat", to: "oc_cam_pc2" },
    { from: "oc_cam_pc2", to: "oc_exposync_beat" },
    { from: "oc_exposync_beat", to: "oc_cut_h2" },
    { from: "oc_cut_h2", to: "oc_title_h2" },
    { from: "oc_title_h2", to: "oc_push_h2" },
    { from: "oc_push_h2", to: "oc_dash_app_beat" },
    { from: "oc_dash_app_beat", to: "oc_teach_consent_bad", on: "granted" },
    { from: "oc_dash_app_beat", to: "oc_teach_consent_safe", on: "refused" },
    { from: "oc_dash_app_beat", to: "oc_teach_consent_safe" },
    { from: "oc_teach_consent_bad", to: "oc_cam_final" },
    { from: "oc_teach_consent_safe", to: "oc_cam_pc_replay2" },
    { from: "oc_cam_pc_replay2", to: "oc_exposync_replay_beat" },
    { from: "oc_exposync_replay_beat", to: "oc_cut_h2r" },
    { from: "oc_cut_h2r", to: "oc_push_h2r" },
    { from: "oc_push_h2r", to: "oc_dash_app_replay_beat" },
    { from: "oc_dash_app_replay_beat", to: "oc_cam_final" },
    { from: "oc_cam_final", to: "oc_summary_beat" },
    { from: "oc_summary_beat", to: "oc_debrief_terminal" },
    { from: "oc_debrief_terminal", to: "oc_quiz" },
    { from: "oc_quiz", to: "oc_debrief" },
  ],
};
